"use client";

import { useEffect, useState } from "react";
import {
  MediaActionTypes,
  useMediaDispatch,
  useMediaSelector,
} from "media-chrome/react/media-store";

import { useMediaPlayerContext } from "./media-player.provider";

/**
 * Tracks whether the main video has been played at least once.
 * Returns true permanently after the first play event.
 *
 * Uses a local state latch rather than reading mediaHasPlayed directly,
 * because media-chrome may reset mediaHasPlayed when the video source
 * changes (e.g. in a video switcher). The latch ensures hasPlayedOnce
 * stays true for the lifetime of the component.
 */
export const useHasPlayedOnce = () => {
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const hasPlayed = useMediaSelector((state) => state?.mediaHasPlayed);

  useEffect(() => {
    if (hasPlayed && !hasPlayedOnce) {
      setHasPlayedOnce(true);
    }
  }, [hasPlayed, hasPlayedOnce]);

  return hasPlayedOnce;
};

/**
 * Playback progress of the current video as a 0–1 fraction.
 *
 * Returns 0 until the duration is known (a live or still-loading stream
 * reports 0 or Infinity), so callers can render a zero-width bar rather
 * than guard the NaN themselves.
 */
export const useProgress = () =>
  useMediaSelector((state) => {
    const duration = state.mediaDuration ?? 0;
    if (!Number.isFinite(duration) || duration <= 0) return 0;
    return Math.min((state.mediaCurrentTime ?? 0) / duration, 1);
  });

/**
 * Hook for building custom overlays.
 *
 * Provides the overlay state logic (show/hide, play action) without
 * any rendering or styling — use this when `MediaPlayer.Overlay` is
 * too opinionated and you need full control over the overlay UI.
 *
 * @example
 * ```tsx
 * const { shouldHide, play, title } = MediaPlayer.useOverlay();
 *
 * <div className={shouldHide ? "opacity-0" : "opacity-100"}>
 *   <h3>{title}</h3>
 *   <button onClick={play}>Play video</button>
 * </div>
 * ```
 */
export const useOverlay = () => {
  const dispatch = useMediaDispatch();
  const isPlaying = useMediaSelector((state) => state.mediaPaused === false);
  const { video, overlay } = useMediaPlayerContext();
  const hasPlayedOnce = useHasPlayedOnce();

  const showWhenPaused = overlay?.showWhenPaused ?? false;
  const shouldHide = isPlaying || (hasPlayedOnce && !showWhenPaused);

  const play = () => {
    dispatch({ type: MediaActionTypes.MEDIA_PLAY_REQUEST });
  };

  return {
    /** Whether the overlay should be hidden */
    shouldHide,
    /** Call to start/resume playback */
    play,
    /** Whether the video is currently playing */
    isPlaying,
    /** Whether the video has been played at least once */
    hasPlayedOnce,
    /** Video title from context */
    title: video?.title,
    /** Whether the overlay should re-show when paused */
    showWhenPaused,
    /** Overlay config from context (hideTitle, children, className) */
    overlay,
  };
};
